// src/context/ProfilesContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";

// ==================== HELPER FUNCTIONS ====================
const extractValue = (value) => {
  if (!value) return null;
  if (typeof value === 'object' && value.value !== undefined) return value.value;
  return value;
};

const formatHeight = (height) => {
  if (!height) return null;
  
  if (typeof height === 'object') {
    if (height.value !== undefined && height.unit !== undefined) {
      return `${height.value} ${height.unit}`;
    } else if (height.value !== undefined) {
      return `${height.value} cm`;
    }
  }
  
  return height;
};

const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  
  try {
    let dob;
    
    if (typeof dateOfBirth !== 'string') return null;
    
    // Try DD-MM-YYYY format (from your JSON)
    const parts = dateOfBirth.split(/[-/.]/);
    if (parts.length === 3) {
      let year, month, day;
      
      // DD-MM-YYYY format
      if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
        day = parseInt(parts[0]);
        month = parseInt(parts[1]) - 1; // Months are 0-indexed
        year = parseInt(parts[2]);
      }
      // YYYY-MM-DD format
      else if (parts[0].length === 4 && parts[1].length === 2 && parts[2].length === 2) {
        year = parseInt(parts[0]);
        month = parseInt(parts[1]) - 1;
        day = parseInt(parts[2]);
      }
      
      if (year && month !== undefined && day) {
        dob = new Date(year, month, day);
        if (!isNaN(dob.getTime())) {
          const today = new Date();
          let age = today.getFullYear() - dob.getFullYear();
          const monthDiff = today.getMonth() - dob.getMonth();
          
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
            age--;
          }
          
          return Math.max(18, Math.min(80, age));
        }
      }
    }
    
    return null;
  } catch (error) {
    console.warn('Error calculating age:', error);
    return null;
  }
};

const validateProfile = (profile) => {
  return (
    profile &&
    profile.profile_id &&
    profile.personal_details?.full_name &&
    profile.personal_details?.full_name.trim() !== ""
  );
};

// Helper function to format phone number with country code
const formatPhoneWithCountryCode = (number, countryCode) => {
  if (!number) return null;
  const digits = number.replace(/\D/g, '');
  if (countryCode) {
    return `${countryCode}${digits}`;
  }
  return number;
};

// ==================== PROFILE PROCESSING ====================
const processProfileData = (profile) => {
  const dateOfBirth = profile.personal_details?.date_of_birth;
  const age = calculateAge(dateOfBirth);
  
  // Extract values from objects
  const heightValue = extractValue(profile.physical_details?.height);
  const formattedHeight = formatHeight(profile.physical_details?.height);
  const weightValue = profile.physical_details?.weight_kg;
  
  // Handle preferences
  const preferencesMinHeight = profile.preferences?.min_height?.value;
  const preferencesMinAge = profile.preferences?.min_age;
  const preferencesMaxAge = profile.preferences?.max_age;
  
  // Process languages
  const languagesKnown = Array.isArray(profile.personal_details?.languages_known) 
    ? profile.personal_details.languages_known.flatMap(lang => 
        typeof lang === 'string' ? lang.split(/[, ]+/).filter(Boolean) : []
      )
    : [];
  
  // Process guardian contact with country code
  const guardianContact = formatPhoneWithCountryCode(
    profile.guardian_details?.contact_number,
    profile.guardian_details?.country_code
  );
  
  const guardianWhatsapp = formatPhoneWithCountryCode(
    profile.guardian_details?.whatsapp_number,
    profile.guardian_details?.whatsapp_country_code
  );
  
  const mobileNumber = formatPhoneWithCountryCode(
    profile.personal_details?.mobile_number,
    profile.guardian_details?.country_code // Assuming same country code for personal mobile
  );
  
  return {
    // Basic Info
    id: profile.profile_id,
    profile_id: profile.profile_id,
    status: profile.status || "active",
    profile_created_date: profile.profile_created_date,
    profile_created_by: profile.profile_created_by,
    profile_created_by_other: profile.profile_created_by_other,
    
    // Guardian Details with country codes
    guardian_name: profile.guardian_details?.guardian_name,
    guardian_relation: profile.guardian_details?.relation,
    guardian_relation_other: profile.guardian_details?.relation_other,
    guardian_contact: guardianContact,
    guardian_contact_raw: profile.guardian_details?.contact_number,
    guardian_country_code: profile.guardian_details?.country_code,
    guardian_whatsapp: guardianWhatsapp,
    guardian_whatsapp_raw: profile.guardian_details?.whatsapp_number,
    guardian_whatsapp_country_code: profile.guardian_details?.whatsapp_country_code,
    
    // Personal Details
    full_name: profile.personal_details?.full_name?.trim(),
    gender: (profile.personal_details?.gender || "").toLowerCase(),
    date_of_birth: dateOfBirth,
    age: age,
    place_of_birth: profile.personal_details?.place_of_birth,
    nationality: profile.personal_details?.nationality,
    mother_tongue: profile.personal_details?.mother_tongue,
    mother_tongue_other: profile.personal_details?.mother_tongue_other,
    languages_known: languagesKnown,
    mobile_number: mobileNumber,
    mobile_number_raw: profile.personal_details?.mobile_number,
    occupation: profile.personal_details?.occupation,
    permanent_address: profile.personal_details?.permanent_address,
    
    // Physical Details
    height: heightValue,
    height_display: formattedHeight,
    height_unit: profile.physical_details?.height?.unit,
    weight_kg: weightValue,
    body_type: profile.physical_details?.body_type,
    body_type_other: profile.physical_details?.body_type_other,
    complexion: profile.physical_details?.complexion,
    complexion_other: profile.physical_details?.complexion_other,
    
    // Religious Details
    religion: profile.religious_details?.religion,
    religion_other: profile.religious_details?.religion_other,
    sect: profile.religious_details?.sect,
    sect_other: profile.religious_details?.sect_other,
    caste: profile.religious_details?.caste,
    caste_other: profile.religious_details?.caste_other,
    religious_practice_level: profile.religious_details?.religious_practice_level,
    practice_level_other: profile.religious_details?.practice_level_other,
    
    // Marital Details
    marital_status: profile.marital_details?.marital_status,
    marital_status_other: profile.marital_details?.marital_status_other,
    children_count: profile.marital_details?.children || 0,
    
    // Education
    highest_degree: profile.education?.highest_degree,
    highest_degree_other: profile.education?.highest_degree_other,
    degree: profile.education?.degree,
    institution: profile.education?.institution,
    other_qualifications: Array.isArray(profile.education?.other_qualifications)
      ? profile.education.other_qualifications
      : [],
    
    // Profession
    job_title: profile.profession?.job_title,
    organization: profile.profession?.organization,
    employment_type: profile.profession?.employment_type,
    employment_type_other: profile.profession?.employment_type_other,
    monthly_income: profile.profession?.monthly_income || 0,
    work_location: profile.profession?.work_location,
    
    // Residence
    residential_status: profile.residence?.residential_status,
    residential_status_other: profile.residence?.residential_status_other,
    city: (profile.residence?.city || "").trim(),
    city_other: profile.residence?.city_other,
    state: profile.residence?.state,
    country: profile.residence?.country || "Pakistan",
    country_other: profile.residence?.country_other,
    
    // Family
    family_type: profile.family?.family_type,
    family_type_other: profile.family?.family_type_other,
    father_profession: profile.family?.father_profession,
    mother_profession: profile.family?.mother_profession,
    family_status: profile.family?.family_status,
    family_status_other: profile.family?.family_status_other,
    siblings_total: profile.family?.siblings?.total || 0,
    brothers_total: profile.family?.siblings?.brothers?.total || 0,
    brothers_married: profile.family?.siblings?.brothers?.married || 0,
    sisters_total: profile.family?.siblings?.sisters?.total || 0,
    sisters_married: profile.family?.siblings?.sisters?.married || 0,
    
    // Health
    blood_group: profile.health?.blood_group,
    blood_group_other: profile.health?.blood_group_other,
    disability: profile.health?.disability || "None",
    disability_other: profile.health?.disability_other,
    medical_conditions: profile.health?.medical_conditions || "None",
    smoking: profile.health?.smoking || "No",
    smoking_other: profile.health?.smoking_other,
    drinking: profile.health?.drinking || "No",
    drinking_other: profile.health?.drinking_other,
    
    // Habits & Lifestyle
    diet: profile.habits_and_lifestyle?.diet,
    diet_other: profile.habits_and_lifestyle?.diet_other,
    hobbies: Array.isArray(profile.habits_and_lifestyle?.hobbies)
      ? profile.habits_and_lifestyle.hobbies
      : [],
    sports_interest: Array.isArray(profile.habits_and_lifestyle?.sports_interest)
      ? profile.habits_and_lifestyle.sports_interest
      : [],
    description: profile.habits_and_lifestyle?.description || "",
    
    // Verification
    verified: {
      cnic: profile.documents_verification?.cnic_verified ?? false,
      education: profile.documents_verification?.education_verified ?? false,
      employment: profile.documents_verification?.employment_verified ?? false,
    },
    
    // Preferences
    preferences_min_age: preferencesMinAge,
    preferences_max_age: preferencesMaxAge,
    preferences_min_height: preferencesMinHeight,
    preferences_min_height_unit: profile.preferences?.min_height?.unit,
    preferences_education: profile.preferences?.education,
    preferences_education_other: profile.preferences?.education_other,
    preferences_profession: profile.preferences?.profession,
    preferences_profession_other: profile.preferences?.profession_other,
    preferences_religion: profile.preferences?.religion,
    preferences_religion_other: profile.preferences?.religion_other,
    preferences_sect: profile.preferences?.sect,
    preferences_sect_other: profile.preferences?.sect_other,
    preferences_caste: profile.preferences?.caste,
    preferences_caste_other: profile.preferences?.caste_other,
    preferences_city: profile.preferences?.city,
    preferences_city_other: profile.preferences?.city_other,
    preferences_country: profile.preferences?.country,
    preferences_marital_status: profile.preferences?.marital_status,
    preferences_marital_status_other: profile.preferences?.marital_status_other,
    preferences_profession_preference: Array.isArray(profile.preferences?.profession_preference)
      ? profile.preferences.profession_preference
      : [],
    preferences_location_preference: Array.isArray(profile.preferences?.location_preference)
      ? profile.preferences.location_preference
      : [],
    preferences_preferred_languages: Array.isArray(profile.preferences?.preferred_languages)
      ? profile.preferences.preferred_languages
      : [],
    preferences_smoking_preference: profile.preferences?.smoking_preference,
    preferences_smoking_preference_other: profile.preferences?.smoking_preference_other,
    preferences_drinking_preference: profile.preferences?.drinking_preference,
    preferences_drinking_preference_other: profile.preferences?.drinking_preference_other,
    
    // Search text
    _searchable_text: [
      profile.personal_details?.full_name,
      profile.personal_details?.occupation,
      profile.residence?.city,
      profile.residence?.country,
      profile.profession?.job_title,
      profile.education?.highest_degree,
      profile.religious_details?.religion,
      profile.religious_details?.sect,
      profile.religious_details?.caste,
      profile.family?.father_profession,
      profile.family?.mother_profession,
      profile.mother_tongue,
      ...languagesKnown,
      ...(profile.habits_and_lifestyle?.hobbies || []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase(),
  };
};

// ==================== CACHE MANAGER ====================
class ProfileCacheManager {
  constructor() {
    this.profileCache = new Map();
    this.filterCache = new Map();
    this.searchCache = new Map();
    this.fileCache = new Map();
    this.cacheTimestamps = new Map();
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  }

  getCacheKey(params = {}) {
    const { limit = 12, offset = 0, search = '', filters = {} } = params;
    const filterString = Object.entries(filters)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}:${value}`)
      .join('|');
    return `${limit}|${offset}|${search}|${filterString}`;
  }

  getProfiles(params) {
    const key = this.getCacheKey(params);
    const cached = this.profileCache.get(key);
    
    if (cached && this.isValid(key)) {
      return cached;
    }
    return null;
  }

  setProfiles(params, profiles) {
    const key = this.getCacheKey(params);
    this.profileCache.set(key, profiles);
    this.cacheTimestamps.set(key, Date.now());
    
    // Clean old cache entries if cache gets too large
    if (this.profileCache.size > 50) {
      const oldestKey = Array.from(this.cacheTimestamps.entries())
        .sort(([, timeA], [, timeB]) => timeA - timeB)[0]?.[0];
      if (oldestKey) {
        this.profileCache.delete(oldestKey);
        this.cacheTimestamps.delete(oldestKey);
      }
    }
  }

  getFile(filename) {
    const cached = this.fileCache.get(filename);
    if (cached && this.isValid(filename)) {
      return cached;
    }
    return null;
  }

  setFile(filename, data) {
    this.fileCache.set(filename, data);
    this.cacheTimestamps.set(filename, Date.now());
  }

  isValid(key) {
    const cachedTime = this.cacheTimestamps.get(key);
    return cachedTime && Date.now() - cachedTime < this.CACHE_DURATION;
  }

  clear() {
    this.profileCache.clear();
    this.filterCache.clear();
    this.searchCache.clear();
    this.fileCache.clear();
    this.cacheTimestamps.clear();
  }

  clearExpired() {
    const now = Date.now();
    for (const [key, timestamp] of this.cacheTimestamps.entries()) {
      if (now - timestamp > this.CACHE_DURATION) {
        this.profileCache.delete(key);
        this.filterCache.delete(key);
        this.searchCache.delete(key);
        this.fileCache.delete(key);
        this.cacheTimestamps.delete(key);
      }
    }
  }
}

// ==================== API SERVICE ====================
const API_BASE_URL = process.env.PUBLIC_URL || '';

const fetchWithTimeout = async (url, options = {}, timeout = 8000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      cache: 'no-cache'
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

const apiService = {
  async fetchIndex() {
    try {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/whiz-public/profiles-json/index.json`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch index: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching index:', error);
      throw error;
    }
  },

  async fetchProfileFile(filename) {
    try {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/whiz-public/profiles-json/${filename}`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch ${filename}: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error fetching ${filename}:`, error);
      throw error;
    }
  },

  // For future API integration
  async fetchProfilesAPI(params = {}) {
    const { limit = 12, offset = 0, search = '', filters = {} } = params;
    
    const queryParams = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      ...filters
    });

    if (search.trim()) {
      queryParams.append('search', search.trim());
    }

    try {
      const response = await fetchWithTimeout(`/api/profiles?${queryParams}`);
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      
      const data = await response.json();
      
      return {
        profiles: Array.isArray(data.profiles) ? data.profiles : [],
        totalCount: typeof data.totalCount === 'number' ? data.totalCount : 0,
        hasMore: data.hasMore || false
      };
    } catch (error) {
      console.error('API fetch error:', error);
      throw error;
    }
  }
};

// ==================== CONTEXT ====================
const ProfilesContext = createContext();

export const ProfilesProvider = ({ children }) => {
  const [allProfiles, setAllProfiles] = useState([]);
  const [displayProfiles, setDisplayProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [ageStats, setAgeStats] = useState({ minAge: 20, maxAge: 40, averageAge: 30 });
  const [totalCount, setTotalCount] = useState(0);
  const [currentParams, setCurrentParams] = useState({
    limit: 12,
    offset: 0,
    search: '',
    filters: {}
  });
  const [hasMore, setHasMore] = useState(false);

  const cacheManager = useMemo(() => new ProfileCacheManager(), []);
  const initializedRef = useRef(false);

  // ==================== LOAD ALL PROFILES ====================
  const loadAllProfiles = useCallback(async () => {
    if (initializedRef.current && allProfiles.length > 0) {
      return allProfiles;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('Loading profiles index...');
      
      // Check cache first
      const cachedIndex = cacheManager.getFile('index');
      let indexData;
      
      if (cachedIndex) {
        indexData = cachedIndex;
      } else {
        indexData = await apiService.fetchIndex();
        cacheManager.setFile('index', indexData);
      }

      const files = indexData.files || [];
      console.log(`Found ${files.length} profile files`);

      if (files.length === 0) {
        setAllProfiles([]);
        setTotalCount(0);
        setDisplayProfiles([]);
        return [];
      }

      // Load files in batches for better performance
      const batchSize = 5;
      let allProfilesData = [];
      
      for (let i = 0; i < files.length; i += batchSize) {
        const batch = files.slice(i, i + batchSize);
        const batchPromises = batch.map(async (filename) => {
          try {
            // Check file cache
            const cachedFile = cacheManager.getFile(filename);
            if (cachedFile) {
              return cachedFile;
            }
            
            const fileData = await apiService.fetchProfileFile(filename);
            cacheManager.setFile(filename, fileData);
            return fileData;
          } catch (fileError) {
            console.warn(`Failed to load ${filename}:`, fileError);
            return null;
          }
        });

        const batchResults = await Promise.all(batchPromises);
        
        for (const result of batchResults) {
          if (Array.isArray(result)) {
            allProfilesData.push(...result);
          } else if (result) {
            allProfilesData.push(result);
          }
        }
      }

      // Process and validate profiles
      const processedProfiles = allProfilesData
        .filter(validateProfile)
        .map(processProfileData)
        .filter(Boolean);

      console.log(`Processed ${processedProfiles.length} valid profiles`);

      // Calculate stats
      const ages = processedProfiles.map(p => p.age).filter(age => age !== null);
      const stats = {
        minAge: ages.length ? Math.min(...ages) : 20,
        maxAge: ages.length ? Math.max(...ages) : 40,
        averageAge: ages.length ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length) : 30
      };

      setAllProfiles(processedProfiles);
      setTotalCount(processedProfiles.length);
      setAgeStats(stats);
      initializedRef.current = true;

      // Set ALL profiles to display initially
      setDisplayProfiles(processedProfiles);
      setHasMore(false);

      return processedProfiles;
    } catch (err) {
      console.error('Error loading all profiles:', err);
      setError({
        message: 'Failed to load profiles',
        details: err.message,
        retry: loadAllProfiles
      });
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [allProfiles, cacheManager]);

  // ==================== FETCH PROFILES WITH PAGINATION ====================
  const fetchProfiles = useCallback(async (params = {}) => {
    const {
      limit = 12,
      offset = 0,
      search = '',
      filters = {},
      useCache = true
    } = params;

    setIsLoading(true);
    setError(null);
    setCurrentParams({ limit, offset, search, filters });

    try {
      // Check cache first
      const cacheKey = { limit, offset, search, filters };
      if (useCache) {
        const cached = cacheManager.getProfiles(cacheKey);
        if (cached) {
          setDisplayProfiles(cached.profiles);
          setTotalCount(cached.totalCount);
          setHasMore(cached.hasMore);
          setIsLoading(false);
          return cached;
        }
      }

      // If no API, filter from allProfiles
      let filteredProfiles = [...allProfiles];

      // Apply search
      if (search.trim()) {
        const searchLower = search.trim().toLowerCase();
        filteredProfiles = filteredProfiles.filter(profile =>
          profile._searchable_text?.includes(searchLower)
        );
      }

      // Apply filters
      if (Object.keys(filters).length > 0) {
        filteredProfiles = filteredProfiles.filter(profile => {
          for (const [key, value] of Object.entries(filters)) {
            if (!value || value === 'all') continue;
            
            switch (key) {
              case 'gender':
                if (value !== 'all' && profile.gender?.charAt(0) !== value.charAt(0)) {
                  return false;
                }
                break;
              case 'ageRange':
                const [minAge, maxAge] = value;
                if (profile.age < minAge || profile.age > maxAge) {
                  return false;
                }
                break;
              case 'city':
                if (!profile.city?.toLowerCase().includes(value.toLowerCase())) {
                  return false;
                }
                break;
              case 'country':
                if (!profile.country?.toLowerCase().includes(value.toLowerCase())) {
                  return false;
                }
                break;
              case 'maritalStatus':
                if (value !== 'all' && profile.marital_status !== value) {
                  return false;
                }
                break;
              case 'religion':
                if (value !== 'all' && profile.religion !== value) {
                  return false;
                }
                break;
              case 'sect':
                if (value !== 'all' && profile.sect !== value) {
                  return false;
                }
                break;
              case 'caste':
                if (value !== 'all' && profile.caste !== value) {
                  return false;
                }
                break;
              case 'verified':
                if (value && (!profile.verified.cnic || !profile.verified.education)) {
                  return false;
                }
                break;
              case 'nationality':
                if (!profile.nationality?.toLowerCase().includes(value.toLowerCase())) {
                  return false;
                }
                break;
              default:
                const profileValue = profile[key];
                if (profileValue && typeof profileValue === 'string') {
                  if (!profileValue.toLowerCase().includes(value.toLowerCase())) {
                    return false;
                  }
                }
            }
          }
          return true;
        });
      }

      // Apply pagination
      const start = offset;
      const end = offset + limit;
      const paginatedProfiles = filteredProfiles.slice(start, end);
      const hasMore = end < filteredProfiles.length;

      const result = {
        profiles: paginatedProfiles,
        totalCount: filteredProfiles.length,
        hasMore
      };

      // Update state
      setDisplayProfiles(paginatedProfiles);
      setTotalCount(filteredProfiles.length);
      setHasMore(hasMore);

      // Cache the result
      cacheManager.setProfiles(cacheKey, result);

      return result;
    } catch (err) {
      console.error('Error fetching profiles:', err);
      setError({
        message: 'Failed to fetch profiles',
        details: err.message,
        retry: () => fetchProfiles(params)
      });
      return { profiles: [], totalCount: 0, hasMore: false };
    } finally {
      setIsLoading(false);
    }
  }, [allProfiles, cacheManager]);

  // ==================== LOAD MORE PROFILES ====================
  const loadMoreProfiles = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    
    try {
      const nextOffset = displayProfiles.length;
      const params = {
        ...currentParams,
        offset: nextOffset,
        limit: 12
      };

      const result = await fetchProfiles(params);
      
      if (result.profiles.length > 0) {
        setDisplayProfiles(prev => [...prev, ...result.profiles]);
      }
      
      setHasMore(result.hasMore);
    } catch (err) {
      console.error('Error loading more profiles:', err);
      setError({
        message: 'Failed to load more profiles',
        details: err.message
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    isLoadingMore, 
    hasMore, 
    displayProfiles.length, 
    currentParams, 
    fetchProfiles,
  ]);

  // ==================== SEARCH PROFILES ====================
  const searchProfiles = useCallback(async (searchQuery) => {
    const params = {
      ...currentParams,
      search: searchQuery,
      offset: 0,
      limit: 12
    };
    
    return await fetchProfiles(params);
  }, [currentParams, fetchProfiles]);

  // ==================== FILTER PROFILES ====================
  const filterProfiles = useCallback(async (filters) => {
    const params = {
      ...currentParams,
      filters,
      offset: 0,
      limit: 12
    };
    
    return await fetchProfiles(params);
  }, [currentParams, fetchProfiles]);

  // ==================== GET PROFILE BY ID ====================
  const getProfileById = useCallback((id) => {
    if (!id) return null;
    return allProfiles.find(profile => 
      profile.id === id || profile.profile_id === id
    );
  }, [allProfiles]);

  // ==================== CLEAR FILTERS ====================
  const clearFilters = useCallback(() => {
    const params = {
      limit: 12,
      offset: 0,
      search: '',
      filters: {}
    };
    
    fetchProfiles(params);
  }, [fetchProfiles]);

  // ==================== REFRESH ====================
  const refreshProfiles = useCallback(() => {
    cacheManager.clear();
    initializedRef.current = false;
    loadAllProfiles();
  }, [cacheManager, loadAllProfiles]);

  // ==================== GET UNIQUE VALUES ====================
  const uniqueValues = useMemo(() => {
    if (allProfiles.length === 0) {
      return {
        cities: [],
        countries: [],
        professions: [],
        religions: [],
        sects: [],
        castes: [],
        education: [],
        maritalStatuses: [],
        residentialStatuses: [],
        familyTypes: [],
        familyStatuses: [],
        employmentTypes: [],
        smokingPreferences: [],
        drinkingPreferences: [],
        nationalities: [],
        languages: [],
      };
    }

    return {
      cities: [...new Set(allProfiles.map(p => p.city).filter(Boolean))].sort(),
      countries: [...new Set(allProfiles.map(p => p.country).filter(Boolean))].sort(),
      professions: [...new Set(allProfiles.map(p => p.job_title).filter(Boolean))].sort(),
      religions: [...new Set(allProfiles.map(p => p.religion).filter(Boolean))].sort(),
      sects: [...new Set(allProfiles.map(p => p.sect).filter(Boolean))].sort(),
      castes: [...new Set(allProfiles.map(p => p.caste).filter(Boolean))].sort(),
      education: [...new Set(allProfiles.map(p => p.highest_degree).filter(Boolean))].sort(),
      maritalStatuses: [...new Set(allProfiles.map(p => p.marital_status).filter(Boolean))].sort(),
      residentialStatuses: [...new Set(allProfiles.map(p => p.residential_status).filter(Boolean))].sort(),
      familyTypes: [...new Set(allProfiles.map(p => p.family_type).filter(Boolean))].sort(),
      familyStatuses: [...new Set(allProfiles.map(p => p.family_status).filter(Boolean))].sort(),
      employmentTypes: [...new Set(allProfiles.map(p => p.employment_type).filter(Boolean))].sort(),
      smokingPreferences: [...new Set(allProfiles.map(p => p.smoking).filter(Boolean))].sort(),
      drinkingPreferences: [...new Set(allProfiles.map(p => p.drinking).filter(Boolean))].sort(),
      nationalities: [...new Set(allProfiles.map(p => p.nationality).filter(Boolean))].sort(),
      languages: [...new Set(allProfiles.flatMap(p => p.languages_known || []))].sort(),
    };
  }, [allProfiles]);

  // ==================== INITIAL LOAD ====================
  useEffect(() => {
    if (!initializedRef.current) {
      loadAllProfiles();
    }

    // Clean up expired cache periodically
    const interval = setInterval(() => {
      cacheManager.clearExpired();
    }, 60000); // Every minute

    return () => clearInterval(interval);
  }, [loadAllProfiles, cacheManager]);

  // ==================== CONTEXT VALUE ====================
  const contextValue = useMemo(() => ({
    // State
    profiles: displayProfiles,
    allProfiles,
    totalCount,
    isLoading,
    isLoadingMore,
    error,
    ageStats,
    uniqueValues,
    hasMore,
    currentParams,
    
    // Actions
    loadAllProfiles,
    fetchProfiles,
    loadMoreProfiles,
    searchProfiles,
    filterProfiles,
    getProfileById,
    clearFilters,
    refreshProfiles,
    
    // Helper functions
    extractValue,
    formatHeight,
    
    // Stats
    verifiedProfiles: allProfiles.filter(p => p.verified.cnic && p.verified.education).length,
    
    // Pagination info
    totalPages: Math.ceil(totalCount / (currentParams.limit || 12)),
    currentPage: Math.floor((currentParams.offset || 0) / (currentParams.limit || 12)) + 1,
  }), [
    displayProfiles,
    allProfiles,
    totalCount,
    isLoading,
    isLoadingMore,
    error,
    ageStats,
    uniqueValues,
    hasMore,
    currentParams,
    loadAllProfiles,
    fetchProfiles,
    loadMoreProfiles,
    searchProfiles,
    filterProfiles,
    getProfileById,
    clearFilters,
    refreshProfiles,
  ]);

  return (
    <ProfilesContext.Provider value={contextValue}>
      {children}
    </ProfilesContext.Provider>
  );
};

// ==================== CUSTOM HOOKS ====================
export const useProfiles = () => {
  const context = useContext(ProfilesContext);
  if (!context) {
    throw new Error("useProfiles must be used within a ProfilesProvider");
  }
  return context;
};

export const useProfileData = (id) => {
  const { getProfileById } = useProfiles();
  return useMemo(() => getProfileById(id), [getProfileById, id]);
};

export const useProfileActions = () => {
  const context = useContext(ProfilesContext);
  
  if (!context) {
    throw new Error("useProfileActions must be used within a ProfilesProvider");
  }

  return {
    search: context.searchProfiles,
    filter: context.filterProfiles,
    loadMore: context.loadMoreProfiles,
    refresh: context.refreshProfiles,
    clearFilters: context.clearFilters,
    getProfile: context.getProfileById,
    fetchProfiles: context.fetchProfiles,
  };
};

export const useProfileState = () => {
  const context = useContext(ProfilesContext);
  
  if (!context) {
    throw new Error("useProfileState must be used within a ProfilesProvider");
  }

  return {
    profiles: context.profiles,
    totalCount: context.totalCount,
    isLoading: context.isLoading,
    isLoadingMore: context.isLoadingMore,
    error: context.error,
    uniqueValues: context.uniqueValues,
    hasMore: context.hasMore,
    currentPage: context.currentPage,
    totalPages: context.totalPages,
    verifiedProfiles: context.verifiedProfiles,
    ageStats: context.ageStats,
  };
};